// import global variables.
import {FOCUS_ELEMENTS, mql, tabindexControl} from '../utils/global';
import * as expand from '../utils/expand';
import * as backdrop from '../utils/backdrop';
import scrollLock from '../utils/scrollLock';

class Toggle {
    /**
     * トグル・アコーディオン
     *
     * @constructor
     * @param {HTMLElement} root ルートとなる要素
     * @param {NodeList} ctrls   コントロールボタンの要素群
     * @param {[type]} contents  コンテンツ部分の要素群
     * @param {Object} options - 設定の変更をする際のオブジェクト
     */
    constructor(root, ctrls, contents, options) {
        const config = {
            type: 'toggle',
            setBackdropLayer: false,
            onlyScreen: '',
            className: {
                heading: 'js-toggle__heading',
                ctrl: 'js-toggle__ctrl',
                open: 'is-open'
            },
            prefix: {
                id: 'toggle-'
            }
        };

        // ルートとなる要素が無い場合は実装しない
        if (!root) {
            return;
        }

        this.config = Object.assign(config, options);
        this.isOpen = false;
        this.isAnimate = false;

        this.root = root;
        this.contents = contents;
        this.headings = !ctrls.length ? Array.from(this.root.querySelectorAll(`.${this.config.className.heading}`)) : [];
        this.ctrls = !ctrls.length ? this.convertHeadingToButton() : ctrls;
        this.backdrop = this.config.setBackdropLayer ? backdrop.createBackdropLayer() : null;
        this.setMediaQuery = this.config.onlyScreen === 'PC' || this.config.onlyScreen === 'SP';
        this.onlyScreen = this.setMediaQuery ? this.config.onlyScreen : '';
        this.currentIndex = 0;

        // インスタンスを要素に設定
        this.root.toggleInstance = this;

        this.init();
        this.addEvent();
    }

    /**
     * init - 初期化
     * @return {Void}
     */
    init() {
        const isInvalidScreen = this.setMediaQuery && mql.state !== this.onlyScreen;

        for (let idx = 0; idx < this.contents.length; idx++) {
            const content = this.contents[idx];
            const ctrl = this.ctrls[idx];
            let isOpen = false;

            // 無効な画面幅の場合は開いたまま初期化を実行
            if (isInvalidScreen) {
                expand.init(ctrl, content, this.config.prefix.id, true);
                this.root.removeChild(ctrl);

                continue;
            }

            // レイヤーを表示する場合は、デフォルトで閉じる
            if (this.config.setBackdropLayer) {
                expand.init(ctrl, content, this.config.prefix.id, false);

                continue;
            }

            // アコーディオンとして実装する場合は[data-default-index]から初期値を取得
            if (this.config.type === 'accordion' && this.root.dataset.defaultIndex) {
                isOpen = idx === parseInt(this.root.dataset.defaultIndex, 10);

                if (isOpen) {
                    this.isOpen = true;
                    this.currentIndex = idx;
                }

            // トグルの場合はopenクラスを持っているか否かで判別
            } else {
                isOpen = content.classList.contains(this.config.className.open);
            }

            expand.init(ctrl, content, this.config.prefix.id, isOpen);
        }
    }

    /**
     * getfocusableElements - フォーカス可能な要素を取得
     * @return {[type]} [description]
     */
    getfocusableElements() {
        const focusableElements = [];

        for (const content of this.contents) {
            for (const element of content.querySelectorAll(FOCUS_ELEMENTS)) {
                focusableElements.push(element);
            }
        }

        return focusableElements;
    }

    /**
     * convertHeadingToButton - 見出し要素をボタン化する
     * @return {Array[NodeList]}
     */
    convertHeadingToButton() {
        const buttonElement = `<button type="button" class="${this.config.className.ctrl}"></button>`;
        const buttons = [];

        for (let i = 0; i < this.headings.length; i++) {
            const heading = this.headings[i];

            heading.insertAdjacentHTML('beforeend', buttonElement);
            buttons.push(heading.lastElementChild);

            for (const child of heading.childNodes) {
                buttons.slice(-1)[0].appendChild(child);
            }
        }

        return buttons;
    }

    /**
     * appendCtrlButton - コントロールボタンを追加する
     * @return {Void}
     */
    appendCtrlButton() {
        // 見出しをボタン化した場合はheadingをctrlに置き換え
        if (this.headings.length) {
            for (let i = 0; i < this.headings.length; i++) {
                this.root.replaceChild(this.ctrls[i], this.headings[i]);
            }

            return;
        }

        // コンテンツの兄要素として追加
        for (let i = 0; i < this.ctrls.length; i++) {
            this.contents[i].insertAdjacentElement('beforebegin', this.ctrls[i]);
        }
    }

    /**
     * addEvent - イベントバインド
     */
    addEvent() {
        for (const ctrl of this.ctrls) {
            ctrl.addEventListener('click', this.toggle.bind(this));
        }

        for (const content of this.contents) {
            content.addEventListener('transitionend', (e) => {
                // height以外のトランジションは検知しない
                if (e.propertyName !== 'height') {
                    return;
                }

                this.transitionAfter(e.target);
            });

            // 背景レイヤーを敷く場合
            if (this.config.setBackdropLayer) {
                content.addEventListener(backdrop.eventName, this.toggleClose.bind(this));
            }
        }

        // 背景レイヤーを敷く場合
        if (this.config.setBackdropLayer) {
            this.root.addEventListener('keydown', (e) => {
                if (!this.isOpen || e.key.indexOf('Esc') === -1) {
                    return;
                }

                // Escapeキー押下で閉じる
                this.toggleClose();
            });
        }

        // PC・SPで設定を変更する場合
        if (this.setMediaQuery) {
            window.addEventListener(mql.event, () => {
                // 開いている場合、閉じる処理
                if (this.isOpen) {
                    backdrop.displayBackdropLayer(false);
                    scrollLock(false);
                    tabindexControl(true, [...this.getfocusableElements(), this.ctrls[this.currentIndex]]);
                }

                // フラグを更新
                this.isAnimate = false;
                this.isOpen = false;

                // SPまたはPCのみ実装する場合かつ現在の画面サイズと実装する画面サイズが一致しない場合はリセット
                if (mql.state !== this.onlyScreen) {
                    this.reset();

                // 再初期化を実行
                } else {
                    this.init();
                    this.appendCtrlButton();
                }
            });
        }

        // ブレイクポイントをまたいだ場合、.js-mypageMenu__contentを閉じる処理
        window.addEventListener(mql.event, () => {
            if (this.root.classList.contains('js-mypageMenu') && this.isOpen) {
                this.toggleClose();
            }
        });
    }

    /**
     * toggle - 開く または 閉じる機能を実行
     * @param  {ClickEvent} event クリックイベントオブジェクト
     * @return {Void}
     */
    toggle(event) {
        const index = [].indexOf.call(this.ctrls, event.currentTarget);

        event.preventDefault();

        // アニメーション中は何もしない
        if (this.isAnimate || index === -1) {
            return;
        }

        // マイページメニューとSPメニューの場合、相互に開いている場合は閉じる処理を実行
        if (mql.state === 'SP') {
            const toggleCloseIfOpen = (selector, menuClass) => {
                document.querySelectorAll(selector).forEach((element) => {
                    const {toggleInstance} = element;

                    if (toggleInstance && toggleInstance.isMenuOpen(menuClass)) {
                        toggleInstance.toggleClose();
                    }
                });
            };

            if (this.root.classList.contains('js-mypageMenu')) {
                toggleCloseIfOpen('.js-searchMenu', 'js-searchMenu');
            }

            if (this.root.classList.contains('js-searchMenu')) {
                toggleCloseIfOpen('.js-mypageMenu', 'js-mypageMenu');
            }
        }

        // アコーディオンの場合 かつ 開いているコンテンツがある場合、すべて閉じてから開く処理を実行
        if (this.config.type === 'accordion' && this.isOpen) {
            this.toggleClose();

            // 開いているコンテンツを閉じるだけの場合はここで終了
            if (this.currentIndex === index) {
                return;
            }
        }

        // カレントを更新
        this.currentIndex = index;

        // トグルの場合 かつ コンテンツが非表示ではない場合、閉じて終了
        if (this.config.type === 'toggle' && !this.contents[this.currentIndex].hidden) {
            this.toggleClose();

            return;
        }

        this.toggleOpen();
    }

    /**
     * isMenuOpen - 指定されたメニューが開いているかどうかを判定
     * @param {String} menuClass メニューのクラス名
     * @return {Boolean}
     */
    isMenuOpen(menuClass) {
        return this.isOpen && this.root.classList.contains(menuClass);
    }

    /**
     * toggleOpen - 開く処理
     * @return {Void}
     */
    toggleOpen() {
        const currentCtrl = this.ctrls[this.currentIndex];
        const currentContent = this.contents[this.currentIndex];

        // フラグを更新
        this.isOpen = true;
        this.isAnimate = true;

        // 背景レイヤーを敷く場合
        if (this.config.setBackdropLayer) {
            tabindexControl(true, [...this.getfocusableElements(), currentCtrl]);
        }

        // 開く処理を実行、false（transition-durtionが設定されていない）場合はトランジション後の処理を実行
        if (!expand.open(currentCtrl, currentContent, this.config.setBackdropLayer)) {
            this.transitionAfter(currentContent);
        }
    }

    /**
     * toggleClose - 閉じる処理
     * @return {Void}
     */
    toggleClose() {
        const currentCtrl = this.ctrls[this.currentIndex];
        const currentContent = this.contents[this.currentIndex];

        // フラグを更新
        this.isOpen = false;
        this.isAnimate = true;

        // 背景レイヤーを敷く場合
        if (this.config.setBackdropLayer) {
            tabindexControl(false, this.getfocusableElements());
        }

        // 閉じる処理を実行、false（transition-durtionが設定されていない）場合はトランジション後の処理を実行
        if (!expand.close(currentCtrl, currentContent, this.config.setBackdropLayer)) {
            this.transitionAfter(currentContent);
        }
    }

    /**
     * transitionAfter - トランジション後の処理
     * @param  {HTMLElement} element 開閉したコンテンツ要素
     * @return {Void}
     */
    transitionAfter(element) {
        this.isAnimate = false;
        expand.transitionAfter(element);
    }

    /**
     * reset - リセット処理
     * @return {Void}
     */
    reset() {
        // コンテンツをすべて表示する
        for (const content of this.contents) {
            content.hidden = false;
        }

        // ヘッダーがない場合（= 見出しのボタン化がされていない）
        if (!this.headings.length) {
            for (const ctrl of this.ctrls) {
                this.root.removeChild(ctrl);
            }

            return;
        }

        // ヘッダーがある場合（= 見出しのボタン化がされている）、見出しに戻す
        for (let i = 0; i < this.headings.length; i++) {
            const heading = this.headings[i];

            heading.removeChild(this.ctrls[i]);

            for (const child of this.ctrls[i].childNodes) {
                heading.insertAdjacentElement('beforeend', child);
            }
        }
    }
}

/**
 * デフォルト実行処理
 * @return {Void}
 */
export default () => {
    const searchButton = '<button type="button" class="js-searchMenu__ctrl"><span class="u-altText">検索メニュー</span></button>';

    // SPメニュー
    document.querySelectorAll('.js-searchMenu').forEach((element) => {
        const contents = element.querySelectorAll('.js-searchMenu__content');

        for (const content of contents) {
            content.insertAdjacentHTML('beforebegin', searchButton);
        }

        return new Toggle(element, element.querySelectorAll('.js-searchMenu__ctrl'), contents, {
            type: 'toggle',
            setBackdropLayer: true,
            onlyScreen: 'SP',
            prefix: {
                id: 'search-menu-'
            }
        });
    });

    // 汎用トグル
    document.querySelectorAll('.js-toggle').forEach((element) => new Toggle(element, element.querySelectorAll('.js-toggle__ctrl'), element.querySelectorAll('.js-toggle__content')));

    // 汎用アコーディオン
    document.querySelectorAll('.js-accordion').forEach((element) => new Toggle(element, element.querySelectorAll('.js-accordion__ctrl'), element.querySelectorAll('.js-accordion__content'), {
        type: 'accordion',
        onlyScreen: '',
        className: {
            heading: 'js-accordion__heading',
            ctrl: 'js-accordion__ctrl'
        },
        prefix: {
            id: 'accordion-'
        }
    }));

    // マイページメニュー
    document.querySelectorAll('.js-mypageMenu').forEach((element) => {
        const contents = element.querySelectorAll('.js-mypageMenu__content');
        let isSP = mql.state === 'SP';

        const toggleInstance = new Toggle(element, element.querySelectorAll('.js-mypageMenu__ctrl'), contents, {
            type: 'toggle',
            setBackdropLayer: isSP, // SPのみ背景レイヤーを敷く
            onlyScreen: '',
            prefix: {
                id: 'mypage-menu-'
            }
        });

        // 画面サイズが変わったら背景レイヤーを再設定
        window.addEventListener(mql.event, () => {
            const newIsSP = mql.state === 'SP';

            if (isSP !== newIsSP) {
                isSP = newIsSP;
                toggleInstance.config.setBackdropLayer = isSP;
            }
        });
    });
};
